import { useContext, useEffect, useState } from 'react';
import {
  Form,
  Button,
  Card,
  Typography,
  Popconfirm,
  Upload,
  Divider,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMessage } from '../../../hooks/useMessage';
import { useStep } from '../../../hooks/useStep';
import { axiosInstance } from '../../../utils/axios';
import { RcFile } from 'antd/es/upload';
import VOGTypes from './VOGTypes.json';
import { FileUrl } from '../../../shared/types/url.interface';
import { uploadFile } from '../../../utils/uploadFile';
import LoadingSpinner from '../util/LoadingSpinner';
import CommentField from '../../CommentField/CommentField';
import { ScreeningContentContext } from '../../../context/ScreeningContentProvider';
import { useTranslation } from 'react-i18next';
import { parseContent } from '../../../utils/parseContent';

const { Paragraph } = Typography;

function CertificateOfConductStep() {
  const { t } = useTranslation(['screening/coc', 'screening/general']);
  const [allowRequestForm] = Form.useForm();
  const [fileUploadForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const { toast } = useMessage();
  const { goToNextStep } = useStep();
  const [cocType, setCocType] = useState<string | undefined>();

  const [hasAllowed, setHasAllowed] = useState(true);
  const [files, setFiles] = useState<RcFile[]>([]);
  const [fileCount, setFileCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const content = useContext(ScreeningContentContext);
  const loadingContent = content === 'loading';
  const [comment, setComment] = useState<string>('');

  const FILE_ERROR_MESSAGE = t('EM-FileNotSaved');
  const SUCCESS_MESSAGE = t('SM-FileSaved');

  useEffect(() => {
    axiosInstance
      .get('/screening/certificate-of-conduct')
      .then((response) => {
        setHasAllowed(response.data.allowCertificateOfConductRequest);
        setCocType(
          VOGTypes.find(
            (item) => item.value === response.data.certificateOfConductType,
          )?.label,
        );
        allowRequestForm.setFieldValue(
          'certificateOfConductType',
          response.data.certificateOfConductType,
        );
        setComment(response.data.comment);
        setFileCount(response.data.pdfFilePath.length);
        setLoadingData(false);
      })
      .catch((error) => {
        if (
          error.response?.status === 404 &&
          error.response.data.message === 'No existing data found'
        ) {
          setHasAllowed(false);
          return;
        }
        toast.error(t('EM-General', { ns: 'screening/general' }));
        setLoadingData(false);
      });
  }, [allowRequestForm, commentForm, toast, t]);

  const handleAllowRequestSubmit = () => {
    axiosInstance
      .post('/screening/certificate-of-conduct/allow-request', {
        allowRequest: true,
        cocType: allowRequestForm.getFieldValue('certificateOfConductType'),
      })
      .then(() => {
        setHasAllowed(true);
        toast.success({ content: t('SM-PermissionSaved') });
      })
      .catch(() => {
        toast.error({
          content: t('EM-General', { ns: 'screening/general' }),
        });
      });
  };

  const handleFileUploadSubmit = async () => {
    setLoading(true);
    if (files[0] === undefined) {
      if (fileCount === 0) {
        setLoading(false);
        return toast.error(t('EM-OneFileRequired'));
      }
      return goToNextStep();
    }

    const fileAmount = files.length;
    await axiosInstance
      .get(`/screening/certificate-of-conduct/fileUrl/${fileAmount}`)
      .then(async (response) => {
        response.data.forEach((file: FileUrl, index: number) => {
          if (!file.url) {
            toast.error(FILE_ERROR_MESSAGE);
            throw new Error(FILE_ERROR_MESSAGE);
          }
          uploadFile(files[index], file.url)
            .then(() => {
              axiosInstance
                .post('screening/certificate-of-conduct/savefile', {
                  path: file.filePath,
                })
                .then(async () => {
                  await goToNextStep();
                })
                .catch(() => {
                  toast.error(
                    parseContent(
                      t('EM-FileNSavedFailed', { ns: 'screening/general' }),
                      [files[index].name],
                    ),
                  );
                });
            })
            .catch(() => {
              toast.error(
                parseContent(
                  t('EM-FileNSavedFailed', { ns: 'screening/general' }),
                  [files[index].name],
                ),
              );
            });
        });
        toast.success(SUCCESS_MESSAGE);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(t('EM-General', { ns: 'screening/general' }));
        throw error;
      });
  };

  const proceedWithoutUpload = () => {
    if (!hasAllowed) return toast.error(t('EM-PermissionRequired'));
    goToNextStep();
  };

  return loadingData || loadingContent ? (
    <LoadingSpinner />
  ) : (
    <>
      <div>
        <Card
          title={content.certificateOfConductCheck.title}
          style={{ maxWidth: 500 }}
        >
          <Paragraph style={{ display: 'inline' }}>
            {content.certificateOfConductCheck.description}
          </Paragraph>
          <Form
            form={allowRequestForm}
            onFinish={handleAllowRequestSubmit}
            layout="vertical"
          >
            <Divider />
            <Form.Item
              name="allowCertificateOfConductRequest"
              initialValue={true}
            >
              <p>
                {t('P-VOGType')} <b>{cocType}</b>
              </p>
              <p>{t('L-Permission')}</p>
              <Popconfirm
                placement="right"
                okText={t('O-Yes', { ns: 'screening/general' })}
                cancelText={t('O-No', { ns: 'screening/general' })}
                title={t('W-Title')}
                description={t('W-PermissionIrreversible')}
                onConfirm={() => {
                  allowRequestForm.submit();
                }}
                overlayStyle={{ width: '250px' }}
              >
                <Button type="primary" disabled={hasAllowed}>
                  {hasAllowed ? t('B-PermissionGiven') : t('B-Permission')}
                </Button>
              </Popconfirm>
            </Form.Item>
          </Form>
          {hasAllowed && (
            <Form form={fileUploadForm} onFinish={handleFileUploadSubmit}>
              <div>
                <p style={{ fontWeight: 'bold' }}>
                  {t('L-Files', { ns: 'screening/general' })}
                </p>
                <p className="fileAmount">
                  {t('P-NumberFilesUploaded', { ns: 'screening/general' })}{' '}
                  {fileCount}
                </p>
                <Form.Item name="fileUpload">
                  <Upload
                    beforeUpload={(file) => {
                      setFiles((oldFiles) => [...oldFiles, file]);
                      return false;
                    }}
                    multiple={true}
                    accept=".pdf"
                    maxCount={5}
                    showUploadList={{ showRemoveIcon: false }}
                  >
                    <Button icon={<UploadOutlined />}>
                      {t('B-SelectFile', { ns: 'screening/general' })}
                    </Button>
                  </Upload>
                </Form.Item>
              </div>
            </Form>
          )}
          <Divider />
          <CommentField
            ApiUrl={'screening/certificate-of-conduct/comment'}
            comment={comment}
          />
        </Card>
        <Button
          style={{ float: 'left', marginTop: 20, marginBottom: 30 }}
          type="link"
          onClick={proceedWithoutUpload}
        >
          {t('B-ContinueFowNow')}
        </Button>
        <Button
          style={{
            float: 'right',
            marginTop: 20,
            marginBottom: 30,
            marginRight: 10,
          }}
          type="primary"
          htmlType="submit"
          onClick={fileUploadForm.submit}
          loading={loading}
        >
          {t('B-ContinueNext')}
        </Button>
      </div>
    </>
  );
}

export default CertificateOfConductStep;
